import axios from 'axios';
import safeStorage from '../../utils/safeStorage';
import { API_URL } from '../../config/env';
import { AppointmentStatus, InvoiceStatus } from '../../types';

// Create an axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = safeStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Session expiry.
 *
 * The token's expiry is only read once, when AuthContext mounts. A token that
 * expires while someone is working turns every later call into a silent 401
 * while the UI still renders as though signed in, so the 401 itself has to be
 * what ends the session. AuthContext registers its logout here; clearing the
 * user is enough for RouteGuard to send the browser to /login.
 */
type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;
let signingOut = false;

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  onUnauthorized = handler;
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config;
    const status = error?.response?.status;

    // Only a 401 on a request that actually carried a token means "your session
    // ended". A 401 from the login form means "wrong password", and the public
    // pay and tracking pages send no token at all — neither should sign anyone
    // out. The re-entrancy flag keeps a page that fires six parallel requests
    // from running the sign-out six times.
    const carriedToken = Boolean(config?.headers?.Authorization);
    const isLoginAttempt = typeof config?.url === 'string' && config.url.includes('/auth/login');

    if (status === 401 && carriedToken && !isLoginAttempt && !signingOut) {
      signingOut = true;
      safeStorage.removeItem('token');
      try {
        onUnauthorized?.();
      } finally {
        // Released on a later tick so the in-flight siblings of this request
        // are all covered by the same sign-out.
        setTimeout(() => {
          signingOut = false;
        }, 0);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * The message a server error is trying to convey.
 *
 * Nest sends `message` as a string for a thrown exception and as an array for
 * validation failures. Reading `message[0]` covers only the second case — on a
 * string it yields the first *character*, which is how a rejected booking used
 * to surface to the customer as "T".
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  if (Array.isArray(message)) {
    const joined = message.filter(Boolean).join(' ');
    return joined || fallback;
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
}

/**
 * Options for the paged list endpoints. Omitting `limit` asks for everything,
 * which is what the admin tables currently do; the total is returned separately
 * so a caller can page without a second request.
 */
export interface ListQuery {
  limit?: number;
  offset?: number;
}

export interface AppointmentListQuery extends ListQuery {
  status?: AppointmentStatus;
  /** Epoch seconds, inclusive, against appointmentDateTime. */
  from?: number;
  to?: number;
}

export interface InvoiceListQuery extends ListQuery {
  status?: InvoiceStatus;
}

export interface PagedResult<T> {
  items: T[];
  /** Total matching rows, ignoring the paging window. */
  total: number;
}

/** Reads the unpaged total the API puts in X-Total-Count. */
export function pagedResult<T>(response: { data: T[]; headers: any }): PagedResult<T> {
  const header = response.headers?.['x-total-count'];
  const total = Number(header);
  return {
    items: response.data,
    total: Number.isFinite(total) ? total : response.data.length,
  };
}

export default api;
